import {makeObservable, observable, action, computed} from 'mobx';
import {StageType} from './TaskExecutionStore';

/**
 * 阶段数据缓存
 *
 * 职责:
 * - 缓存三个阶段的结果数据 (COLDSTART, EVOLVE, REPORT)
 * - 提供统一的数据访问接口
 * - 替代原 TaskRunningStore 中的 stage_data, evolveStageData, resultStageData
 *
 * 设计原则:
 * - 单一职责:只管理数据缓存,不管理状态/UI/加载
 * - 用 Map 替代三个独立字段,消除重复逻辑
 * - 类型安全:泛型支持不同阶段的数据类型
 */
export class StageDataCache {
    /**
     * 阶段数据缓存
     * 替代原来的:
     * - stage_data (冷启动)
     * - evolveStageData (演化)
     * - resultStageData (结果)
     */
    cache = new Map<StageType, any>();

    constructor() {
        makeObservable(this, {
            cache: observable,
            cachedStages: computed,
            hasAnyData: computed,
            coldStartData: computed,
            evolveData: computed,
            resultData: computed,
            set: action,
            update: action,
            clear: action,
            reset: action,
            setColdStartData: action,
            setEvolveData: action,
            setResultData: action
        });
    }

    /**
     * 获取所有已缓存的阶段
     */
    get cachedStages(): StageType[] {
        return Array.from(this.cache.keys());
    }

    /**
     * 是否有任何缓存数据
     */
    get hasAnyData(): boolean {
        return this.cache.size > 0;
    }

    /**
     * 冷启动阶段数据 (兼容旧代码)
     */
    get coldStartData(): any | null {
        return this.get('COLDSTART');
    }

    /**
     * 演化阶段数据 (兼容旧代码)
     */
    get evolveData(): any | null {
        return this.get('EVOLVE');
    }

    /**
     * 结果阶段数据 (兼容旧代码)
     */
    get resultData(): any | null {
        return this.get('REPORT');
    }

    /**
     * 获取指定阶段的数据
     */
    get(stage: StageType): any | null {
        return this.cache.get(stage) || null;
    }

    /**
     * 是否有指定阶段的数据
     */
    has(stage: StageType): boolean {
        return this.cache.has(stage);
    }

    /**
     * 设置指定阶段的数据
     */
    set(stage: StageType, data: any) {
        this.cache.set(stage, data);
    }

    /**
     * 更新指定阶段的部分数据
     */
    update(stage: StageType, partialData: Partial<any>) {
        const existingData = this.cache.get(stage) || {};
        this.cache.set(stage, {
            ...existingData,
            ...partialData
        });
    }

    /**
     * 清除指定阶段的数据
     */
    clear(stage?: StageType) {
        if (stage) {
            this.cache.delete(stage);
        } else {
            this.cache.clear();
        }
    }

    /**
     * 重置所有缓存
     */
    reset() {
        this.cache.clear();
    }

    /**
     * 设置冷启动数据 (兼容旧代码)
     */
    setColdStartData(data: any) {
        this.set('COLDSTART', data);
    }

    /**
     * 设置演化数据 (兼容旧代码)
     */
    setEvolveData(data: any) {
        this.set('EVOLVE', data);
    }

    /**
     * 设置结果数据 (兼容旧代码)
     */
    setResultData(data: any) {
        this.set('REPORT', data);
    }
}
